import { AfterViewInit, Component, OnDestroy, Renderer2, inject } from '@angular/core';
import { StateDeclaration, StateObject, StateService, UIRouterGlobals, Transition, TransitionService } from '@uirouter/angular';
import { IAirline } from '../types/airline';
import { ILanguage } from '../types/language';
import { ActivityEvents, ActivityService } from '../commons/services/activity.service';
import { auditTime, filter, map, merge, pairwise, startWith, tap } from 'rxjs';
import { AuthService } from '../commons/services/auth.service';
import { AirlineService } from 'app/commons/services/airline.service';
import { LanguagesService } from 'app/commons/services/languages.service';
import UIkit from "uikit";
import { StorageService } from 'app/commons/services/storageService.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { currentLanguageToken, currentTenantToken, currentUserToken, languagesToken, menuOptionsToken, sessionTimeoutToken, tenantsToken } from './app.root.resolvers';

@Component({
  selector: 'ng2-root',
  templateUrl: "./ng2Root.view.html",
  standalone: false,
  providers: [ActivityService],
  styles: [
    ".em-sidebar.default { z-index: 1104 !important; }",
    "@media only screen and (max-width: 1219px) { .em-sidebar.default.active { z-index: 1204 !important; }}"
  ]
})
export class Ng2RootComponent implements AfterViewInit, OnDestroy {

  private readonly _renderer = inject(Renderer2);
  private readonly airlineService = inject(AirlineService);
  private readonly authService = inject(AuthService);
  private readonly languagesService = inject(LanguagesService);
  private readonly activityService = inject(ActivityService);
  private readonly $state = inject(StateService);
  private readonly $globals = inject(UIRouterGlobals);
  private readonly _storage = inject(StorageService);

  private _userIdleModal: UIkit.UIkitModalElement;
  private _isContextTransitioning: boolean = false;
  private _cumulativeChangesWhileTransitioning: { tenant?: IAirline, language?: ILanguage } = {};

  public readonly airlines = inject(tenantsToken);
  public currentAirline = inject(currentTenantToken);
  public currentLanguage = inject(currentLanguageToken);
  public readonly currentUser = inject(currentUserToken);
  public readonly languages = inject(languagesToken);
  public readonly menuOptions = inject(menuOptionsToken);
  public readonly sessionTimeout = inject(sessionTimeoutToken);

  constructor() {
    this._configureContextChangeSubscription();
    this.authService.startLogoutListener();
    this._configureSessionTimeout();
  }

  private _configureContextChangeSubscription():void {
    merge(
      this.languagesService.observeLanguageChange()
        .pipe(map(language => ({ language: language, tenant: null } as { tenant?: IAirline, language?: ILanguage }))),
      this.airlineService.observeTenantChange()
        .pipe(map(tenant => ({ tenant: tenant, language: null } as { tenant?: IAirline, language?: ILanguage })))
    )
      .pipe(
        takeUntilDestroyed(),
        tap(changes => {
          if (!this._isContextTransitioning) return;
          this._cumulativeChangesWhileTransitioning.language = changes.language || this._cumulativeChangesWhileTransitioning.language || null;
          this._cumulativeChangesWhileTransitioning.tenant = changes.tenant || this._cumulativeChangesWhileTransitioning.tenant || null;
        }),
        filter(() => !this._isContextTransitioning),
        startWith({ tenant: null, language: null }),
        pairwise(),
        auditTime(50)
      )
      .subscribe(([previous, current]) => this._handleContextChange({
        tenant: current.tenant || previous.tenant || null,
        language: current.language || previous.language || null
      }, false));
  }

  private _configureSessionTimeout(): void {
    if (this.sessionTimeout === -1) return;
    this.activityService.on(ActivityEvents.UserHasTimedOut)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this._userIdleModal.hide();
        this.authService.logout();
      });
    this.activityService.on(ActivityEvents.UserIsIdle)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this._showExpirationMessage());
    this.activityService.configure({ timeToIdle: this.sessionTimeout }).start();
  }

  private _showExpirationMessage(): void {
    this._userIdleModal = UIkit.modal('#ng2-root-session-timeout-modal');
    this._userIdleModal.show();
  }

  public ngAfterViewInit() {
    this._renderer.addClass(document.body, 'loaded');
  }

  public ngOnDestroy(): void {
    this.authService.stopLogoutListener();
    if (this.activityService.isRunning) this.activityService.stop();
  }

  public logout(): void {
    this.authService.logout();
  }

  public airlineChange(airline: IAirline): void {
    this._handleContextChange({ tenant: airline });
  }

  public languageChange(language: ILanguage): void {
    this._handleContextChange({ language: language });
  }

  private _handleContextChange(changes: { tenant?: IAirline, language?: ILanguage }, setStorageBeforeStart = true): void {
    const tenantHandler = this._stageCommitHandler("currentAirline", changes.tenant, v => this._storage.set.currentAirline(v), v => this.airlineService.currentAirline = v);
    const languageHandler = this._stageCommitHandler("currentLanguage", changes.language, v => this._storage.set.currentLanguage(v), v => this.languagesService.setCurrentLanguage(v));
    if (setStorageBeforeStart) {
      tenantHandler.stage();
      languageHandler.stage();
    }
    this._isContextTransitioning = true;
    this._redirect2(this.$globals.$current)
      .then(() => {
        tenantHandler.commit(this._cumulativeChangesWhileTransitioning.tenant);
        languageHandler.commit(this._cumulativeChangesWhileTransitioning.language);
      })
      .catch(() => {
        tenantHandler.restore(this._cumulativeChangesWhileTransitioning.tenant);
        languageHandler.restore(this._cumulativeChangesWhileTransitioning.language);
      })
      .finally(() => {
        this._isContextTransitioning = false;
        this._cumulativeChangesWhileTransitioning = {};
      });
  }

  private _stageCommitHandler<T, K extends keyof T>(this: T, key: K, changes: T[K], stage: (value: T[K]) => void, save: (value: T[K]) => void) {
    const previous: T[K] = this[key];
    if (changes) this[key] = changes;
    return {
      stage: () => {
        if (!changes) return;
        stage(changes);
      },
      commit: (alt: T[K] | null) => {
        if (!changes && !alt) return;
        const finalChanges = alt || changes;
        save(finalChanges);
      },
      restore: (alt: T[K]) => {
        if (!changes && !alt) return;
        this[key] = previous;
        save(previous);
      }
    }
  }

  private _redirect2(current: StateObject): Promise<void> {
    const toState = this._getToStates(current);
    let exited = false;
    return new Promise((resolve, reject) => {
      const promise = this.$state.go(toState, null, { reload: true });
      const rootTransition = promise.transition as Transition;
      const transitionsService = rootTransition.injector().get<TransitionService>(TransitionService);
      const deregisterForceReload = transitionsService.onBefore(
        {},
        transition => {
          if (transition.originalTransition() !== rootTransition) return;
          const routerRootState = transition.router.stateRegistry.root();
          const reloadState = transition.options().reloadState;
          if (!!reloadState && reloadState == routerRootState) return;
          return transition.targetState().withOptions({ reload: true, reloadState: routerRootState });
        }
      );
      const deregisterOnExit = transitionsService.onExit(
        { exiting: current.self.name },
        () => {
          exited = true;
          resolve();
        }
      );
      promise.finally(() => {
        deregisterForceReload();
        deregisterOnExit();
        if (!exited) reject();
      });
    });
  }

  private _getToStates(current: StateObject): StateDeclaration {
    const parentMatcher = /^\w+[Ll]ist$/;
    const urlMatcher = /\/list/;
    const includesList = current.path.find((path) => path.name.match(parentMatcher) || path.self.url.match(urlMatcher));
    return includesList ? includesList.self : current.self;
  }
}
